import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Film, Loader2, RefreshCw, Plus, Package, Image as ImageIcon, Video, Music } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../context/ToastContext';
import { authenticatedFetch } from '../utils/api';
import { API_HOST } from '../utils/constants';
import OfficialModelArkLibraryPanel from '../components/assets/OfficialModelArkLibraryPanel';
import DreaminaProxyLibraryPanel from '../components/assets/DreaminaProxyLibraryPanel';

const PROVIDERS = {
  domestic: {
    id: 'domestic',
    title: '国内 GuaVideo',
    subtitle: '原 2.0 代理素材库 · 保留使用',
    apiBase: '/api/resource/guavideo-asset',
    groupName: 'shadonggua',
    groupDescription: 'shadonggua asset group',
    requiresGroup: true,
    accent: 'var(--color-accent-primary)',
  },
  overseas: {
    id: 'overseas',
    title: '海外 EngGuaVideo',
    subtitle: '旧海外 2.0 素材库 · 历史数据保留',
    apiBase: '/api/resource/eng-guavideo-asset',
    groupName: 'shadonggua-overseas',
    groupDescription: 'shadonggua overseas asset group',
    requiresGroup: false,
    accent: 'var(--color-success)',
  },
  dreamina_proxy: {
    id: 'dreamina_proxy',
    title: 'Dreamina v3 新代理',
    subtitle: '海外 2.0 / Fast 新任务专用虚拟素材组',
    proxyLibrary: true,
    accent: 'var(--color-success)',
  },
  volcengine_sd25: {
    id: 'volcengine_sd25',
    title: '国内 Seedance 官方',
    subtitle: '国内官方 2.5 / 2.0 / Fast / Mini 共用国内库',
    official: true,
    accent: 'var(--color-accent-primary)',
  },
  byteplus_sd25: {
    id: 'byteplus_sd25',
    title: '海外 Dreamina 2.5 素材库',
    subtitle: '沿用 2.5 绑定，代理 API Key 鉴权；与海外 2.0 库分开',
    official: true,
    accent: 'var(--color-success)',
  },
};

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number') return String(value);
  }
  return '';
};

const normalizeAssetList = (payload) => {
  if (Array.isArray(payload)) return payload;
  const candidates = [
    payload?.Items,
    payload?.items,
    payload?.Assets,
    payload?.assets,
    payload?.AssetSet,
    payload?.assetSet,
    payload?.List,
    payload?.list,
    payload?.Data,
    payload?.data,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
    if (Array.isArray(candidate?.Items)) return candidate.Items;
    if (Array.isArray(candidate?.items)) return candidate.items;
  }
  return [];
};

const getAssetId = (item) => firstText(item?.Id, item?.id, item?.AssetId, item?.assetId);
const getAssetName = (item) => firstText(item?.Name, item?.name, item?.Title, item?.title, '未命名');
const getAssetType = (item) => firstText(item?.AssetType, item?.assetType, item?.Type, item?.type, 'Image');
const getAssetStatus = (item) => firstText(item?.Status, item?.status, item?.State, item?.state, 'Unknown');
const getAssetUrl = (item) => firstText(item?.URL, item?.Url, item?.url, item?.SourceUrl, item?.sourceUrl);
const RESOURCE_TYPE_CODE_BY_ASSET_TYPE = Object.freeze({
  Image: 'user_image',
  Video: 'user_video',
  Audio: 'user_audio',
});

const AssetManagePage = () => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();

  const [activeProvider, setActiveProvider] = useState('domestic');
  const provider = useMemo(() => PROVIDERS[activeProvider] || PROVIDERS.domestic, [activeProvider]);
  const [groupInfo, setGroupInfo] = useState(null);
  const [assets, setAssets] = useState(null);
  const [loading, setLoading] = useState(false);
  const [createUrl, setCreateUrl] = useState('');
  const [createName, setCreateName] = useState('');
  const [createType, setCreateType] = useState('Image');
  const [creating, setCreating] = useState(false);
  const [refreshSequence, setRefreshSequence] = useState(0);
  const loadGroupInfo = useCallback(async (signal) => {
    if (provider.official || provider.proxyLibrary) return;
    try {
      const res = await authenticatedFetch(`${API_HOST}${provider.apiBase}/group-info`, { signal });
      if (!signal?.aborted) setGroupInfo(res);
    } catch (e) {
      if (signal?.aborted) return;
      console.error(e);
      setGroupInfo(null);
    }
  }, [provider.apiBase, provider.official, provider.proxyLibrary]);

  const loadAssets = useCallback(async (signal) => {
    if (provider.official || provider.proxyLibrary) return;
    setLoading(true);
    try {
      const res = await authenticatedFetch(`${API_HOST}${provider.apiBase}/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pageNum: 1, pageSize: 100 }),
        signal,
      });
      if (!signal?.aborted) setAssets(res);
    } catch (e) {
      if (signal?.aborted) return;
      console.error(e);
      setAssets(null);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [provider.apiBase, provider.official, provider.proxyLibrary]);

  useEffect(() => {
    const abort = new AbortController();
    setGroupInfo(null);
    setAssets(null);
    loadGroupInfo(abort.signal);
    loadAssets(abort.signal);
    return () => abort.abort();
  }, [loadGroupInfo, loadAssets, refreshSequence]);

  const assetItems = useMemo(() => normalizeAssetList(assets), [assets]);

  const handleCreateGroup = async () => {
    try {
      const res = await authenticatedFetch(`${API_HOST}${provider.apiBase}/create-group`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: provider.groupName, description: provider.groupDescription }),
      });
      if (res?.groupId) {
        toast.success(`素材组创建成功: ${res.groupId}`);
        setRefreshSequence((value) => value + 1);
      }
    } catch (e) {
      toast.error(`创建失败: ${e.message}`);
    }
  };

  const handleCreateAsset = async () => {
    if (!createUrl.trim()) {
      toast.warning('请输入素材 URL');
      return;
    }
    setCreating(true);
    try {
      const res = await authenticatedFetch(`${API_HOST}${provider.apiBase}/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: createUrl.trim(),
          name: createName.trim() || '未命名',
          assetType: createType,
          typeCode: RESOURCE_TYPE_CODE_BY_ASSET_TYPE[createType] || 'user_image',
          source: 'admin_asset_library',
          sourceDetail: `${provider.id}_manual_create`,
        }),
      });
      const assetId = firstText(res?.assetId, res?.engGuaVideoAssetId);
      if (assetId) {
        toast.success(`素材创建成功: ${assetId}`);
        setCreateUrl('');
        setCreateName('');
        setRefreshSequence((value) => value + 1);
      }
    } catch (e) {
      toast.error(`创建失败: ${e.message}`);
    } finally {
      setCreating(false);
    }
  };

  const typeIcon = (type) => {
    const value = firstText(type).toLowerCase();
    if (value === 'image') return <ImageIcon size={14} />;
    if (value === 'video') return <Video size={14} />;
    if (value === 'audio') return <Music size={14} />;
    return <Film size={14} />;
  };

  return (
    <div className="h-full w-full overflow-y-auto custom-scrollbar" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      <div className="mx-auto max-w-5xl space-y-6 px-6 py-8">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Package size={24} style={{ color: provider.accent }} />
            <div>
              <h1 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>素材库管理</h1>
              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>国内 2.5 使用国内官方库；海外 2.5 沿用独立的 2.5 绑定，通过代理 API Key 入库。海外 2.0 素材组保持独立，不混用素材 ID。</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setRefreshSequence((value) => value + 1)}
            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors bg-[var(--color-bg-tertiary)] hover:bg-[var(--color-bg-secondary)]"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            <RefreshCw size={13} /> 刷新
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5" role="group" aria-label="素材库渠道">
          {Object.values(PROVIDERS).map((item) => {
            const active = item.id === activeProvider;
            return (
              <button
                key={item.id}
                type="button"
                aria-pressed={active}
                onClick={() => setActiveProvider(item.id)}
                className={`rounded-2xl p-4 text-left transition-colors border ${active
                  ? 'bg-[var(--color-bg-secondary)]'
                  : 'bg-[var(--color-bg-tertiary)] border-[var(--color-border-primary)] hover:border-[var(--color-border-hover)]'}`}
                style={active ? { borderColor: item.accent } : undefined}
              >
                <div className="text-sm font-bold" style={{ color: active ? item.accent : 'var(--color-text-primary)' }}>{item.title}</div>
                <div className="mt-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>{item.subtitle}</div>
              </button>
            );
          })}
        </div>

        {provider.official ? <OfficialModelArkLibraryPanel key={provider.id} provider={provider} refreshSequence={refreshSequence} />
          : provider.proxyLibrary ? <DreaminaProxyLibraryPanel refreshSequence={refreshSequence} /> : <>
        <div className="rounded-2xl p-5" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-xs font-bold" style={{ color: 'var(--color-text-muted)' }}>
                {provider.requiresGroup ? 'Asset Group' : '人物素材入库'}
              </div>
              <div className="mt-1 text-sm font-semibold" style={{ color: provider.accent }}>{provider.title}</div>
            </div>
            <span className="rounded-full px-3 py-1 text-2xs font-bold" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: provider.accent }}>
              {activeProvider === 'domestic' ? '国内' : '海外'}
            </span>
          </div>
          {!provider.requiresGroup ? (
            <div>
              <div className="rounded-lg px-3 py-2 text-sm" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: provider.accent }}>
                普通角色图片可直接入库
              </div>
              <p className="mt-2 text-2xs" style={{ color: 'var(--color-text-muted)' }}>
                经过真人认证的素材可额外绑定专属 group_id，普通 AI 角色无需创建素材组。
              </p>
            </div>
          ) : groupInfo?.configured ? (
            <div>
              <div className="break-all rounded-lg px-3 py-2 font-mono text-sm" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: provider.accent }}>{groupInfo.groupId}</div>
              <p className="mt-2 text-2xs" style={{ color: 'var(--color-text-muted)' }}>素材组已配置，当前 Tab 的素材会进入该组。</p>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <span className="text-sm" style={{ color: theme.colors.error }}>未配置素材组</span>
              <button
                onClick={handleCreateGroup}
                className="rounded-lg px-4 py-2 text-xs font-bold transition-opacity hover:opacity-90"
                style={{ backgroundColor: provider.accent, color: 'var(--color-accent-contrast)' }}
              >
                创建 {provider.groupName} 素材组
              </button>
            </div>
          )}
        </div>

        <div className="space-y-3 rounded-2xl p-5" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
          <div className="text-xs font-bold" style={{ color: 'var(--color-text-muted)' }}>上传素材到 {provider.title}</div>
          <input
            value={createUrl}
            onChange={(event) => setCreateUrl(event.target.value)}
            placeholder="素材公网 URL（图片/视频/音频）"
            className="w-full rounded-xl px-4 py-2.5 text-sm outline-none"
            style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
          />
          <div className="flex gap-3">
            <input
              value={createName}
              onChange={(event) => setCreateName(event.target.value)}
              placeholder="名称（可选）"
              className="min-w-0 flex-1 rounded-xl px-4 py-2.5 text-sm outline-none"
              style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
            />
            <div className="flex shrink-0 gap-1.5">
              {['Image', 'Video', 'Audio'].map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setCreateType(type)}
                  className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-medium transition-all"
                  style={{
                    backgroundColor: createType === type ? provider.accent : 'var(--color-bg-tertiary)',
                    color: createType === type ? 'var(--color-accent-contrast)' : 'var(--color-text-muted)',
                    border: `1px solid ${createType === type ? provider.accent : 'var(--color-border-primary)'}`,
                  }}
                >
                  {typeIcon(type)} {type}
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={handleCreateAsset}
            disabled={creating}
            className="flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-bold transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: provider.accent, color: 'var(--color-accent-contrast)' }}
          >
            {creating ? <><Loader2 size={14} className="animate-spin" /> 上传中...</> : <><Plus size={14} /> 上传素材</>}
          </button>
        </div>

        <div className="rounded-2xl p-5" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
          <div className="mb-3 flex items-center justify-between">
            <div className="text-xs font-bold" style={{ color: 'var(--color-text-muted)' }}>{provider.title} 素材列表</div>
            <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>{assetItems.length} 个素材</span>
          </div>
          {loading ? (
            <div className="py-8 text-center"><Loader2 size={20} className="mx-auto animate-spin" style={{ color: 'var(--color-text-muted)' }} /></div>
          ) : assetItems.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
              {assetItems.map((item, index) => {
                const status = getAssetStatus(item);
                const isActive = status.toLowerCase() === 'active';
                const previewUrl = getAssetUrl(item);
                const assetType = getAssetType(item);
                const typeLower = assetType.toLowerCase();
                return (
                  <div
                    key={getAssetId(item) || index}
                    className="overflow-hidden rounded-xl transition-all hover:brightness-105"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)' }}
                  >
                    <div className="relative aspect-square overflow-hidden" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
                      {previewUrl && typeLower === 'image' ? (
                        <img src={previewUrl} alt={getAssetName(item)} className="h-full w-full object-cover" loading="lazy" />
                      ) : previewUrl && typeLower === 'video' ? (
                        <video src={previewUrl} className="h-full w-full object-cover" muted preload="metadata" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center" style={{ color: 'var(--color-text-muted)' }}>
                          {typeIcon(assetType)}
                        </div>
                      )}
                      <span
                        className="absolute right-1.5 top-1.5 rounded-full px-1.5 py-0.5 text-2xs font-medium"
                        style={{ backgroundColor: isActive ? 'var(--color-success)' : 'var(--color-warning)', color: 'var(--color-accent-contrast)' }}
                      >
                        {status}
                      </span>
                      <span
                        className="absolute left-1.5 top-1.5 rounded-full px-1.5 py-0.5 text-2xs font-medium"
                        style={{ backgroundColor: 'var(--color-bg-overlay)', color: 'var(--color-text-primary)' }}
                      >
                        {assetType}
                      </span>
                    </div>
                    <div className="p-2.5">
                      <div className="truncate text-xs font-medium" style={{ color: 'var(--color-text-primary)' }}>{getAssetName(item)}</div>
                      <div className="mt-0.5 truncate font-mono text-2xs" style={{ color: 'var(--color-text-muted)' }}>{getAssetId(item)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-8 text-center text-sm" style={{ color: 'var(--color-text-muted)' }}>暂无素材</div>
          )}
        </div>
        </>}
      </div>
    </div>
  );
};

export default AssetManagePage;
